import { Component, inject, output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AuthService } from '../../_core/auth.service';
import { PasswordValidators } from '../../settings/account/account';
import { Spinner } from '../../_core/spinner';
import { InputText } from 'primeng/inputtext';
import { Router } from '@angular/router';
import { Core } from '../../_core/core';
import { HomeS } from '../../_core/home';
import { Notification } from '../../_core/notification';
import { Settings } from '../../_core/settings';
import { Metering } from '../../_core/metering';
import { BillingService } from '../../_core/billings';
import { MessageModule } from 'primeng/message';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-change-password',
  imports: [
    ButtonModule,
    CardModule,
    FloatLabelModule,
    InputIconModule,
    IconFieldModule,
    InputText,
    MessageModule,
    ReactiveFormsModule,
    CommonModule,
  ],
  templateUrl: './change-password.html',
  styleUrl: './change-password.scss',
})
export class ChangePassword {
  readonly navigate = output<string>();

  private $auth = inject(AuthService);
  private $core = inject(Core);
  private $home = inject(HomeS);
  private $notification = inject(Notification);
  private $settings = inject(Settings);
  private $metering = inject(Metering);
  private $billing = inject(BillingService);
  private fBuilder = inject(FormBuilder);
  private $spinner = inject(Spinner);
  private router = inject(Router);

  public passwordForm: FormGroup = this.initPassForm();
  public showCPassword: boolean = false;
  public showPassword: boolean = false;

  public error: string = '';

  public backToLogin(): void {
    this.$core.clear();
    this.$home.clear();
    this.$notification.clearNotifications();
    this.$settings.clear();
    this.$metering.clear();
    this.$billing.clear();
    this.$auth.clearSession();
    this.navigate.emit('LOGIN');
    void this.router.navigate(['/', 'login'], { replaceUrl: true });
  }

  public async changePassword(): Promise<void> {
    if (this.passwordForm.invalid) {
      return;
    }
    this.error = '';
    this.$spinner.show();
    const DATA = {
      currentPassword: this.passwordForm.controls['currentPassword'].value,
      newPassword: this.passwordForm.controls['newPassword'].value,
    };
    try {
      const isChanged = await this.$auth.changePassword(DATA);

      if (isChanged === true) {
        this.loadingUser();
      } else {
        this.error = 'No fue posible cambiar la contraseña';
        this.$spinner.hide();
      }
    } catch (err: any) {
      this.$spinner.hide();
      this.error = err.error.message;
    }
  }

  private async loadingUser(): Promise<void> {
    const loaded = await this.$auth.isUserDataLoaded();
    if (!loaded) {
      this.$spinner.hide();
      this.error = 'No se pudo continuar tras cambiar la contraseña';
      return;
    }

    const BUILDINGS = await this.$core.fetchBuildings();
    this.$spinner.hide();
    if (BUILDINGS.length === 1) {
      this.router.navigate(['/', 'loading']);
    } else {
      this.router.navigate(['/', 'propiedades']);
    }
  }

  private initPassForm(): FormGroup {
    return this.fBuilder.group(
      {
        currentPassword: ['', Validators.required],
        newPassword: ['', [Validators.required, PasswordValidators.strength()]],
        repeatPassword: ['', [Validators.required, PasswordValidators.strength()]],
      },
      { validators: PasswordValidators.match('newPassword', 'repeatPassword') },
    );
  }

  get passErrors() {
    const control = this.passwordForm.get('newPassword');

    // Si el control no existe o no tiene valor, todo en false
    if (!control || !control.value) {
      return {};
    }

    // Si el control es válido (errors === null), devolvemos todo en true
    if (control.valid) {
      return {
        hasMinLength: true,
        hasMaxLength: true,
        hasUpper: true,
        hasLower: true,
        hasNumber: true,
        hasSymbol: true,
      };
    }

    // Si tiene errores, devolvemos el objeto de errores o vacío si es otro tipo de error
    return control.errors?.['passwordStrength'] || {};
  }

  get passwordsMatch() {
    return (
      !this.passwordForm.hasError('passwordsMatch') &&
      this.passwordForm.get('repeatPassword')?.value !== ''
    );
  }
}
